<template>
  <div class="page-content">
    <div class="search-bar">
      <div class="search-input-wrapper">
        <i class="el-icon-search search-icon"></i>
        <input
          v-model="searchText"
          type="text"
          class="search-input"
          placeholder="搜索群名称/群ID..."
          @keyup.enter="handleSearch"
        />
      </div>
      <div class="action-buttons">
        <button class="btn btn-secondary">
          <i class="el-icon-filter"></i>
          筛选
        </button>
        <button class="btn btn-primary">
          <i class="el-icon-plus"></i>
          新增群聊
        </button>
      </div>
    </div>

    <div class="card">
      <div class="table-wrapper">
        <table class="data-table">
          <thead>
            <tr>
              <th>群ID</th>
              <th>群信息</th>
              <th>群主</th>
              <th>成员数</th>
              <th>状态</th>
              <th>创建时间</th>
              <th>操作</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="group in groups" :key="group.id" class="table-row">
              <td class="id-cell">#{{ group.id }}</td>
              <td>
                <div class="group-info">
                  <div class="group-avatar">
                    <img v-if="group.headImage" :src="group.headImage" alt="avatar" />
                    <i v-else class="el-icon-chat-dot-square"></i>
                  </div>
                  <div class="group-detail">
                    <span class="group-name">{{ group.name }}</span>
                    <span class="group-notice">{{ group.notice || '暂无群公告' }}</span>
                  </div>
                </div>
              </td>
              <td>
                <div class="owner-info">
                  <div class="owner-avatar">
                    <i class="el-icon-user-solid"></i>
                  </div>
                  <span class="owner-name">{{ group.ownerName || '-' }}</span>
                </div>
              </td>
              <td>
                <span class="member-count">{{ group.memberCount || 0 }} 人</span>
              </td>
              <td>
                <div class="status-cell" :class="group.isBanned ? 'status-blocked' : 'status-active'">
                  <span class="status-dot"></span>
                  <span class="status-text">{{ group.isBanned ? '已封禁' : '正常' }}</span>
                </div>
              </td>
              <td>
                <span class="time-text">{{ group.createdTime || '-' }}</span>
              </td>
              <td>
                <div class="action-cell">
                  <button
                    class="action-btn"
                    :class="group.isBanned ? 'action-success' : 'action-warning'"
                    :title="group.isBanned ? '解封群聊' : '封禁群聊'"
                    @click="toggleBlock(group)"
                  >
                    <i :class="group.isBanned ? 'ri-lock-unlock-line' : 'ri-forbid-line'"></i>
                  </button>
                  <button class="action-btn action-info" title="查看详情" @click="viewDetail(group)">
                    <i class="ri-eye-line"></i>
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <div class="pagination-bar">
        <span class="pagination-info">显示 {{ (currentPage - 1) * pageSize + 1 }} 到 {{ Math.min(currentPage * pageSize, total) }} 条，共 {{ total }} 条</span>
        <div class="pagination">
          <button class="page-btn" :disabled="currentPage === 1" @click="currentPage--">上一页</button>
          <button
            v-for="page in visiblePages"
            :key="page"
            class="page-btn"
            :class="{ active: page === currentPage, ellipsis: page === '...' }"
            :disabled="page === '...'"
            @click="page !== '...' && (currentPage = page)"
          >
            {{ page }}
          </button>
          <button class="page-btn" :disabled="currentPage === totalPages" @click="currentPage++">下一页</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'GroupManagement',
  data() {
    return {
      groups: [],
      searchText: '',
      currentPage: 1,
      pageSize: 10,
      total: 0
    }
  },
  computed: {
    totalPages() {
      return Math.ceil(this.total / this.pageSize)
    },
    visiblePages() {
      const pages = []
      const total = this.totalPages
      const current = this.currentPage
      if (total <= 5) {
        for (let i = 1; i <= total; i++) pages.push(i)
      } else {
        if (current <= 3) {
          pages.push(1, 2, 3, 4, '...', total)
        } else if (current >= total - 2) {
          pages.push(1, '...', total - 3, total - 2, total - 1, total)
        } else {
          pages.push(1, '...', current - 1, current, current + 1, '...', total)
        }
      }
      return pages
    }
  },
  mounted() {
    this.loadGroups()
  },
  watch: {
    currentPage() {
      this.loadGroups()
    }
  },
  methods: {
    async loadGroups() {
      try {
        const response = await this.$http({
          url: '/admin/group/list',
          method: 'get',
          params: {
            page: this.currentPage,
            size: this.pageSize,
            keyword: this.searchText
          }
        })
        this.groups = response.list || response
        this.total = response.total || this.groups.length
      } catch (error) {
        this.$message.error('加载群聊列表失败')
      }
    },
    handleSearch() {
      this.currentPage = 1
      this.loadGroups()
    },
    async toggleBlock(group) {
      const action = group.isBanned ? 'unblock' : 'block'
      try {
        await this.$http({
          url: `/admin/group/${action}/${group.id}`,
          method: 'post'
        })
        group.isBanned = !group.isBanned
        this.$message.success(group.isBanned ? '已封禁群聊' : '已解封群聊')
      } catch (error) {
        this.$message.error('操作失败')
      }
    },
    viewDetail(group) {
      this.$message.info('查看群聊详情：' + group.name)
    }
  }
}
</script>

<style scoped>
@import url('https://cdn.jsdelivr.net/npm/remixicon@4.2.0/fonts/remixicon.css');

.page-content {
  max-width: 100%;
}

.search-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
  gap: 16px;
}

.search-input-wrapper {
  position: relative;
  max-width: 320px;
  width: 100%;
}

.search-icon {
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: #94a3b8;
  font-size: 16px;
}

.search-input {
  width: 100%;
  padding: 10px 12px 10px 40px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  font-size: 14px;
  outline: none;
  transition: all 0.2s;
  background: #fff;
}

.search-input:focus {
  border-color: #07c160;
  box-shadow: 0 0 0 3px rgba(7, 193, 96, 0.1);
}

.search-input::placeholder {
  color: #94a3b8;
}

.action-buttons {
  display: flex;
  gap: 12px;
}

.btn {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 10px 16px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  border: none;
}

.btn i {
  font-size: 16px;
}

.btn-secondary {
  background: #fff;
  border: 1px solid #e2e8f0;
  color: #64748b;
}

.btn-secondary:hover {
  background: #f8fafc;
  border-color: #07c160;
  color: #07c160;
}

.btn-primary {
  background: #07c160;
  color: #fff;
  box-shadow: 0 2px 8px rgba(7, 193, 96, 0.25);
}

.btn-primary:hover {
  background: #06ae56;
  box-shadow: 0 4px 12px rgba(7, 193, 96, 0.35);
}

.card {
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  overflow: hidden;
}

.table-wrapper {
  overflow-x: auto;
  max-height: calc(100vh - 380px);
  overflow-y: auto;
}

.data-table {
  width: 100%;
  border-collapse: collapse;
  text-align: left;
}

.data-table thead {
  background: #f8fafc;
}

.data-table th {
  padding: 16px 24px;
  font-size: 12px;
  font-weight: 600;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  border-bottom: 1px solid #e2e8f0;
}

.data-table td {
  padding: 16px 24px;
  border-bottom: 1px solid #f1f5f9;
  font-size: 14px;
  color: #334155;
}

.table-row:hover {
  background: #f9fafb;
}

.id-cell {
  color: #94a3b8;
  font-weight: 500;
}

.group-info {
  display: flex;
  align-items: center;
  gap: 12px;
}

.group-avatar {
  width: 40px;
  height: 40px;
  border-radius: 8px;
  background: #f1f5f9;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  color: #64748b;
  font-size: 20px;
  flex-shrink: 0;
}

.group-avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.group-detail {
  display: flex;
  flex-direction: column;
  max-width: 200px;
}

.group-name {
  font-weight: 600;
  color: #0f172a;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.group-notice {
  font-size: 12px;
  color: #94a3b8;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.owner-info {
  display: flex;
  align-items: center;
  gap: 8px;
}

.owner-avatar {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: #ecfdf5;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #07c160;
  font-size: 12px;
}

.owner-name {
  color: #334155;
}

.member-count {
  color: #334155;
}

.status-cell {
  display: flex;
  align-items: center;
  gap: 8px;
}

.status-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
}

.status-active .status-dot {
  background: #10b981;
}

.status-active .status-text {
  color: #059669;
}

.status-blocked .status-dot {
  background: #ef4444;
}

.status-blocked .status-text {
  color: #dc2626;
}

.time-text {
  color: #64748b;
  font-size: 13px;
}

.action-cell {
  display: flex;
  gap: 8px;
}

.action-btn {
  width: 32px;
  height: 32px;
  border-radius: 6px;
  border: none;
  background: transparent;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
}

.action-btn i {
  font-size: 18px;
}

.action-btn:hover {
  background: #f1f5f9;
}

.action-warning:hover {
  color: #f59e0b;
}

.action-success:hover {
  color: #10b981;
}

.action-info:hover {
  color: #3b82f6;
}

.pagination-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 24px;
  background: #f8fafc;
  border-top: 1px solid #e2e8f0;
}

.pagination-info {
  font-size: 14px;
  color: #64748b;
}

.pagination {
  display: flex;
  gap: 4px;
}

.page-btn {
  min-width: 32px;
  height: 32px;
  padding: 0 12px;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  background: #fff;
  color: #64748b;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s;
}

.page-btn:hover:not(:disabled) {
  border-color: #07c160;
  color: #07c160;
}

.page-btn.active {
  background: #07c160;
  border-color: #07c160;
  color: #fff;
}

.page-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.page-btn.ellipsis {
  border: none;
  background: transparent;
  cursor: default;
}
</style>